import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Page<T> {
  items: T[];
}

const countOf = (data: Page<unknown> | unknown[] | null | undefined): number | null => {
  if (data === null || data === undefined) return null;
  return Array.isArray(data) ? data.length : (data.items?.length ?? 0);
};

/**
 * Network domain landing page — an at-a-glance inventory of the physical and logical
 * resources with direct links, rather than repeating the sidebar. Each count is
 * permission-gated and fails soft.
 */
export function NetworkOverview(): JSX.Element {
  const t = useT();
  const { can } = useAuth();

  const sites = useAsync(
    () => (can('site:read') ? api.get<Page<unknown>>('/sites?limit=1') : Promise.resolve(null)),
    [],
  );
  const edges = useAsync(
    () =>
      can('edge_device:read')
        ? api.get<Page<unknown>>('/edge-devices?limit=1')
        : Promise.resolve(null),
    [],
  );
  const relays = useAsync(
    () => (can('relay:read') ? api.get<unknown[]>('/relays') : Promise.resolve(null)),
    [],
  );

  // /sites and /edge-devices are paginated with a total; fall back to page length.
  const siteCount = sites.data
    ? ((sites.data as { total?: number }).total ?? countOf(sites.data))
    : null;
  const edgeCount = edges.data
    ? ((edges.data as { total?: number }).total ?? countOf(edges.data))
    : null;
  const relayCount = countOf(relays.data);

  const tile = (
    labelKey: string,
    value: number | null,
    to: string,
    allowed: boolean,
  ): JSX.Element => (
    <Link className="tile card--link" to={to}>
      <span className="tile__label">{t(labelKey)}</span>
      <span className="tile__value">{allowed ? (value ?? '…') : '—'}</span>
    </Link>
  );

  return (
    <section>
      <div className="page-head">
        <h1>{t('nav.groups.network')}</h1>
      </div>

      <div className="tiles">
        {tile('nav.sites', siteCount, '/sites', can('site:read'))}
        {tile('nav.devices', edgeCount, '/devices', can('edge_device:read'))}
        {tile('nav.relays', relayCount, '/relays', can('relay:read'))}
      </div>

      <div className="card">
        <h2>{t('ops.quickLinks')}</h2>
        <div className="row-actions">
          <Link className="btn btn--ghost" to="/sites">
            {t('nav.sites')}
          </Link>
          <Link className="btn btn--ghost" to="/devices">
            {t('nav.devices')}
          </Link>
          <Link className="btn btn--ghost" to="/topology">
            {t('nav.topology')}
          </Link>
          <Link className="btn btn--ghost" to="/address-pools">
            {t('nav.addressPools')}
          </Link>
        </div>
      </div>
    </section>
  );
}
